<script setup lang="ts">
import { computed, onMounted, watch, ref, onUnmounted } from 'vue'
//Components
import DroneSpinner from 'Common/components/elements/feedback/DroneSpinner.vue'
//Classes
import { Vector3 } from 'three'
import { VisualizerScene } from 'Common/components/elements/visualizer/visualizerScene'
import { WaypointMarkerModel } from '@/common/components/elements/visualizer/waypointMarker'
import { waypointEventCallback } from 'Common/components/elements/visualizer/waypointEventCallback'

//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import { useFilterStore } from 'Store/filterStore'
//Util
import { storeToRefs } from 'pinia'
//API
import { useGetAirCraftsFiltered } from 'API/queries/iris/aircraft'
//Types
import { WaypointpMarkerType } from 'Common/types/elements/visualizer/marker'
import type { ImageType } from 'Modules/types/pages/imageReview/imageType'

const props = defineProps<PropTypes>()
const filterStore = useFilterStore()
const imagesStore = useImageReviewStore()

const sceneLoaded = ref<boolean>(false)
const visualizerParent = ref<HTMLElement>(null)
const rootRef = ref<HTMLElement>(null)
const labelRef = ref<HTMLElement>(null)
const hoveredWaypoint = ref<WaypointpMarkerType>(null)
const showAllWaypoints = ref<boolean>(false)

const { data: aircraftData, isLoading: isAircraftDetailLoading } = useGetAirCraftsFiltered({
  variables: {
    filterQueryParams: filterStore.queryParamObj,
  },
})
const { currentImage, images } = storeToRefs(imagesStore.imagesStore)

interface PropTypes {
  pushImageRoute: (imageUuid: string) => void
}

const isLoading = () => {
  if (isAircraftDetailLoading.value === true || hasWaypoint.value === false || sceneLoaded.value === false) {
    return true
  }
  return false
}
const hasWaypoint = computed<boolean>(() => {
  return currentImage.value !== undefined && images.value?.length > 0
})
const hasStlUrl = computed<boolean>(() => {
  return aircraftData.value?.[0]?.stlUrl !== undefined
})

let visualizerScene

function waypointMouseInCallback(waypointModel: WaypointMarkerModel) {
  const rootElement = rootRef.value
  const labelElement = labelRef.value
  const clientHeight = rootElement.clientHeight
  const clientWidth = rootElement.clientWidth
  const waypointWorldPosition = new Vector3()
  const screenPosition = waypointModel.mesh.getWorldPosition(waypointWorldPosition)
  screenPosition.project(visualizerScene.camera)

  const translateX = screenPosition.x * clientWidth * 0.5
  const translateY = -screenPosition.y * clientHeight * 0.5

  const xOffset = translateX <= 0 ? 30 : -280
  const yOffset = translateY >= 0 ? -115 : 15

  hoveredWaypoint.value = waypointModel.marker

  labelElement.style.transform = `translateX(${translateX + xOffset}px) translateY(${translateY + yOffset}px)`
  labelElement.classList.remove('hidden')
}

function waypointMouseOutCallback() {
  const labelElement = labelRef.value
  labelElement.classList.add('hidden')
}

function waypointClickedCallback(waypointModel: WaypointMarkerModel) {
  if (waypointModel.marker.meta?.imageUuid) {
    props.pushImageRoute(waypointModel.marker.meta?.imageUuid)
  }
}

function sceneLoadedCallback() {
  sceneLoaded.value = true
}

function startVisualization() {
  if (hasStlUrl.value === true) {
    generateVisualizerScene(aircraftData.value?.stlUrl)
  }

  if (hasWaypoint.value === true) {
    generateWaypointMarkers(images)
  }
}

function generateWaypointMarkers(defects) {
  if (!visualizerScene) return
  const wayPointsArray = []
  let selectedWaypoint = null
  images.value.forEach((image: ImageType) => {
    const camera_angle = image.cameraAngle
    const camera_position = image.cameraPosition
    const wpInfo: WaypointpMarkerType = {
      locx: camera_position.x,
      locy: camera_position.y,
      locz: camera_position.z,
      heading: -camera_angle.z,
      cameraangle: -camera_angle.y,
      wporder: image.waypointOrder,
      name: image.waypointName,
      meta: {
        imageUuid: image.imageUuid,
        waypointUuid: image.waypointUuid,
      },
    }
    wayPointsArray.push(wpInfo)
    if (image.imageUuid == currentImage.value.imageUuid) {
      selectedWaypoint = wpInfo
    }
  })
  visualizerScene.initializeGroup('waypoints', wayPointsArray, waypointEventCallback, selectedWaypoint)
  visualizerScene.initializeSpotLight()
}

// TODO: add defect data
// function generateHeatmapMarkers(defects) {
//   if(!visualizerScene) return
//   const heatmapMarkersArray = []
//   defects.forEach((defect) => {
//     const hmInfo: HeatmapMarkerType = {
//       locx: defect.defectCentroid.x,
//       locy: defect.defectCentroid.y,
//       locz: defect.defectCentroid.z,
//       heading: 0,
//       cameraangle: 0,
//       name: defect.imageUuid,
//       severity: defect.annotationType,
//     }
//     heatmapMarkersArray.push(hmInfo)
//   })
//   visualizerScene.initializeGroup('heatmap', heatmapMarkersArray, markerEventCallback)
// }

function generateVisualizerScene(aircraftModelUrl) {
  if (visualizerScene) return
  visualizerScene = new VisualizerScene(aircraftData.value?.[0].stlUrl, {
    onLoadedCallback: sceneLoadedCallback,
    clickedCallback: waypointClickedCallback,
    mouseInCallback: waypointMouseInCallback,
    mouseOutCallback: waypointMouseOutCallback,
    sceneOptions: {
      ambientLightOptions: { intensity: 2 },
      hideGrid: false,
      hideMeshPlane: false,
      hidemeasurementPlane: false,
    },
  })
  visualizerScene.init(visualizerParent.value, rootRef.value)
}

watch(showAllWaypoints, () => {
  if (visualizerScene) {
    if (showAllWaypoints.value === false) {
      visualizerScene.moveToMarker(visualizerScene.markers.selectedMarkerModel.mesh, { updateSpotLight: true })
    }

    visualizerScene.markers.setShowAllMarkers(showAllWaypoints.value)
  }
})

watch(currentImage, () => {
  if (currentImage.value !== undefined) {
    const wp = visualizerScene.markers.getWaypointByImage(currentImage.value.imageUuid)
    visualizerScene.markers.setSelectedMarker(wp)
    visualizerScene.moveToMarker(visualizerScene.markers.selectedMarkerModel.mesh, { updateSpotLight: true })
  }
})

watch([hasWaypoint, hasStlUrl], () => {
  startVisualization()
})
onMounted(() => {
  startVisualization()
})

onUnmounted(() => {
  visualizerParent.value = null
  rootRef.value = null
  labelRef.value = null
  visualizerScene.destroy()
  visualizerScene = null
})
</script>

<template>
  <div class="h-full relative">
    <template v-if="isLoading()">
      <div
        id="inspectionImg"
        class="absolute top-0 h-full w-full text-white bg-neutral-900 flex flex-col items-center justify-center px-1 pb-5 z-10"
      >
        <DroneSpinner />
        <span>Loading Waypoint Visualizer Data</span>
      </div>
    </template>
    <div
      id="visualizer-toolbar"
      class="text-white absolute z-40 top-0 left-0 h-16 w-full px-4 py-4 flex items-center justify-end"
    >
      <span class="px-2 rounded bg-black bg-opacity-75">
        <el-checkbox v-model="showAllWaypoints" class="text-white" label="Show All Waypoints" size="large" />
      </span>
    </div>
    <div id="visualizer-parent" ref="visualizerParent" class="h-full">
      <div id="visualizer-root" ref="rootRef" class="h-full w-full relative">
        <div
          ref="labelRef"
          class="absolute hidden top-[50%] left-[50%] h-28 w-min bg-black bg-opacity-75 text-white z-50 rounded"
        >
          <div class="h-28 w-max relative">
            <div class="grid grid-rows-2 p-3 grid-cols-[25%_75%] gap-y-2">
              <span class="font-semibold">Name:</span>
              <span>{{ hoveredWaypoint?.name }}</span>
              <span class="font-semibold">Order:</span>
              <span>{{ hoveredWaypoint?.wporder }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
